import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';
import { IReporteBase } from 'modelos/src';

/**
 * ActivosLayerManager
 *
 * Manager para renderizar activos/vehículos en el mapa.
 *
 * Características:
 * - Icono simple (pin-activo)
 * - Soporta clustering
 * - Color de cluster: verde (#4CAF50)
 * - Más simple que VehiculosLayerManager (una sola capa, sin orientación)
 *
 * NOTA: Este es un ejemplo de manager SIMPLE sin complejidades adicionales.
 */
@Injectable({
  providedIn: 'root',
})
export class ActivosLayerManager extends BaseEntityLayerManager<
  IReporteBase<'Tracker 4G'>
> {
  // ========== CONFIGURACIÓN ==========
  readonly entityType = 'activos';
  readonly supportsClustering = true;
  readonly clusterColor = '#4CAF50'; // Verde

  // IDs de layers
  private mainLayerId = 'activos-layer';
  private unclusteredLayerId = 'activos-unclustered-layer';

  // ========== RENDERIZACIÓN ==========

  /**
   * Convierte un activo a un Feature de GeoJSON
   */
  override entityToFeature(
    reporte: IReporteBase<'Tracker 4G'>,
  ): GeoJSON.Feature<GeoJSON.Point> {
    this.storeEntity(reporte);

    const geojson = reporte.valores?.geojson;

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: geojson?.coordinates || [0, 0],
      },
      properties: {
        id: reporte._id,
        idActivo: reporte.activo?._id || reporte._id,
        identificacion: reporte.activo?.identificacion || 'S/N',
        categoria: reporte.activo?.categoria,
        funcion: reporte.activo?.funcion,
      },
    };
  }

  /**
   * Icono simple para activos
   */
  override getIconExpression(): string {
    return 'pin-activo-icon';
  }

  // ========== CREACIÓN DE LAYERS ==========

  /**
   * Crea los layers para activos
   *
   * Más simple que vehículos: solo un layer principal + clustering
   */
  protected override createLayers(): void {
    if (!this.map) return;

    // 1. LAYERS DE CLUSTERING
    if (this.supportsClustering) {
      this.createStandardClusterLayers();

      // Layer de activos NO CLUSTERED (cuando clustering está ON)
      this.addLayer({
        id: this.unclusteredLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': this.getIconExpression(),
          'icon-size': 0.212, // PNG 165x165 → 35px (35/165 = 0.212)
          'icon-allow-overlap': true,
          'icon-padding': 0,
          visibility: 'none', // Inicialmente oculto
        },
      });
    }

    // 2. MAIN LAYER (activos sin clustering)
    this.addLayer({
      id: this.mainLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getIconExpression(),
        'icon-size': 0.212, // PNG 165x165 → 35px (35/165 = 0.212)
        'icon-allow-overlap': true,
        'icon-padding': 0,
      },
    });
  }

  // ========== CLUSTERING ==========

  /**
   * Maneja la visibilidad de layers según clustering
   */
  protected override updateClusteringVisibility(): void {
    if (!this.map || !this.supportsClustering) return;

    if (this.clusteringEnabled) {
      // CLUSTERING ON: mostrar clusters (respetando filtro), ocultar main layer
      // IMPORTANTE: Usar this.isVisible en lugar de true para respetar el estado del filtro
      this.setLayerVisibility(
        `${this.entityType}-cluster-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(this.unclusteredLayerId, this.isVisible);
      this.setLayerVisibility(this.mainLayerId, false);
    } else {
      // CLUSTERING OFF: ocultar clusters, mostrar main layer (respetando filtro)
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, false);
      this.setLayerVisibility(`${this.entityType}-cluster-count-layer`, false);
      this.setLayerVisibility(this.unclusteredLayerId, false);
      // IMPORTANTE: Usar this.isVisible en lugar de true para respetar el estado del filtro
      this.setLayerVisibility(this.mainLayerId, this.isVisible);
    }
  }

  /**
   * Helper para cambiar visibilidad
   */
  private setLayerVisibility(layerId: string, visible: boolean): void {
    if (!this.map) return;

    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(
        layerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }

  // ========== VISIBILIDAD GENERAL ==========

  /**
   * Override de setVisibility
   */
  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;

    if (this.clusteringEnabled && this.supportsClustering) {
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, visible);
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        visible,
      );
      this.setLayerVisibility(this.unclusteredLayerId, visible);
    } else {
      this.setLayerVisibility(this.mainLayerId, visible);
    }
  }
}
